import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { Body1, TableCell, TableRow, type TableCellProps } from '@fluentui/react-components';
import { DueDateLabel } from '@shared/components/dates/DueDateLabel';
import { SmartTableCell } from '@shared/components/tables/SmartTableCell';
import ArchiveTableAction from '@shared/components/tables/ArchiveTableAction';
import DragHandle from '@shared/components/tables/DragHandle';
import { useReorderableRow } from '@shared/hooks/useReorderableRow.hook';
import type { TResolvedItemsMap } from '@modules/items/types/items.types';
import { TASK_DRAG_TYPE, TASK_LIST_LINKED_ITEMS_ENABLED } from '../constants';
import { resolveLinkedItems } from '../linkedItems.helper';
import type { ETaskStatus, TTaskListItem } from '../types';
import { getTaskPath } from '../paths';
import TaskAssigneeLabel from './TaskAssigneeLabel';
import TaskLinkedItemLinks from './TaskLinkedItemLinks';
import TaskPriorityLabel from './TaskPriorityLabel';
import TaskStatusMenu from './TaskStatusMenu';

type TProps = {
  task: TTaskListItem;
  resolvedItems: TResolvedItemsMap;
  number: number;
  projectId: string;
  revision: number;
  getCellProps: (column: string) => TableCellProps;
  statusPending: boolean;
  onStatusChange: (task: TTaskListItem, status: ETaskStatus) => void;
  onArchive: (task: TTaskListItem) => void;
  canReorder: boolean;
  disabledReason: string;
  onReorder: (sourceId: string, targetId: string) => void;
  /** Rows of a table section reorder only within it. */
  section?: string;
};
const TaskRow = ({
  task,
  resolvedItems,
  number,
  projectId,
  revision,
  getCellProps,
  statusPending,
  onStatusChange,
  onArchive,
  canReorder,
  disabledReason,
  onReorder,
  section,
}: TProps) => {
  const { search } = useLocation();
  const { rowRef, handleRef, isDragging, isOver } = useReorderableRow({
    type: TASK_DRAG_TYPE,
    id: task._id,
    scope: `${projectId}:${revision}:${section ?? ''}`,
    canReorder: canReorder && !disabledReason,
    onReorder,
  });
  const linkedItems = resolveLinkedItems(task.linkedItems, resolvedItems);
  return (
    <TableRow ref={rowRef} className={classNames({ 'opacity-40': isDragging, '!bg-neutralBg-3': isOver })}>
      <TableCell {...getCellProps('number')}>
        <div className="flex items-center gap-2 text-neutralFg-3">
          {canReorder && <DragHandle ref={handleRef} disabledReason={disabledReason} />}
          <Body1>{number}</Body1>
        </div>
      </TableCell>
      <SmartTableCell
        cellProps={getCellProps('title')}
        variant={{ kind: 'link', href: getTaskPath(projectId, task._id, search), label: task.title }}
        openMode="sameTab"
        tooltip={{ threshold: 70 }}
        after={task.permissions.canArchive && <ArchiveTableAction label="Archive" onArchive={() => onArchive(task)} />}
        stackedBelow={
          TASK_LIST_LINKED_ITEMS_ENABLED && linkedItems.length > 0 && <TaskLinkedItemLinks linkedItems={linkedItems} />
        }
      />
      <TableCell {...getCellProps('status')}>
        <TaskStatusMenu
          size="small"
          status={task.status}
          disabled={statusPending}
          onStatusChange={task.permissions.canEdit ? (status) => onStatusChange(task, status) : undefined}
        />
      </TableCell>
      <TableCell {...getCellProps('assignee')}>
        <TaskAssigneeLabel assignee={task.assignee} />
      </TableCell>
      <TableCell {...getCellProps('dueDate')}>
        <DueDateLabel variant="compact" dueDate={task.dueDate} due={task.due} />
      </TableCell>
      <TableCell {...getCellProps('priority')}>
        <TaskPriorityLabel priority={task.priority} variant="compact" />
      </TableCell>
    </TableRow>
  );
};
export default TaskRow;
